import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { NextMatchPreloader, MobileNextMatchPreloader } from '../ContentPreloaders';
import FallbackContentAnimation from '../FallbackContentAnimation';
import {
  StyledNextMatch,
  StyledNextMatchInner,
  StyledContainer,
  StyledLink,
  StyledTypePill,
  StyledDatePill,
  StyledKitsRow,
  StyledKitSide,
  StyledKitStage,
  StyledCrestFade,
  StyledKitName,
  StyledVs,
  StyledLiveScore,
  StyledLiveGoal,
  StyledLiveX,
  StyledEmpty,
  StyledButtonsContainer,
  StyledFallbackContainer,
  StyledInGame,
} from './styled';
import LiveMatch from '../LiveMatch';
import TeamShirt from '../TeamShirt';
import { upcomingCalendarList } from '../../redux/selector/Team';
import { calendarData } from '../../redux/selector/League';
import { isAuth, isPremiumUser } from '../../redux/selector/User';
import { isOpponentPremium } from '../../redux/selector/Home';
import SectionHeading from '../SectionHeading';
import { formatMatchWhen, useDisplayTimezone } from '../../service/localTime';
import {
  liveMinuteLabel,
  liveScores,
  isMatchLive,
  matchOpenPath,
} from '../../service/liveMatch';

const kitStyle = {
  position: 'relative',
  width: '118px',
  height: 'auto',
  maxHeight: '138px',
  top: 'auto',
  right: 'auto',
  left: 'auto',
  bottom: 'auto',
  margin: 0,
  zIndex: 1,
};

const lookupCrest = (data, name, id) => {
  if (data?.teamId === id || data?.teamName === name) return data?.symbol;
  return data?.rankingTable?.find((team) => team._id === id || team.name === name)?.symbol || null;
};

const liveTypeLabel = (match) => {
  if (match?.friendly) return 'Friendly';
  if (match?.training) return 'Practice';
  if (match?.domestic_cup) return 'Cup';
  return 'League';
};

const NextMatch = ({ data, isLoading, teamKit, opponentKit }) => {
  useDisplayTimezone();
  const history = useHistory();
  const isCurrentPremium = useSelector(isPremiumUser);
  const isOpponentUserPremium = useSelector(isOpponentPremium);
  const isAuthenticated = useSelector(isAuth);
  const calendarEvents = useSelector(upcomingCalendarList);
  const fixtures = useSelector(calendarData);
  const isCurrentMatchAvailable = isMatchLive(data.matchRunning);
  const matchData = isCurrentMatchAvailable ? 'matchRunning' : 'next_match';
  const noNextMatch = isEmpty(data.next_match);
  const homeTeam = data?.[matchData]?.home_team?.name;
  const homeTeamId = data?.[matchData]?.home_team?._id;
  const awayTeam = data?.[matchData]?.away_team?.name;
  const awayTeamId = data?.[matchData]?.away_team?._id;
  const matchDate = data?.[matchData]?.match_date;
  const isDomesticCup = data?.[matchData]?.domestic_cup;
  const timezoneDateRelatedToUTC = formatMatchWhen(matchDate, {
    timezone: data?.timezone,
    isCup: Boolean(isDomesticCup),
  });
  const isCurrentUserHome = data.teamId === homeTeamId;
  const isCurrentUserAway = data.teamId === awayTeamId;
  const isOpponentUserHome = data.teamId !== homeTeamId;
  const isOpponentUserAway = data.teamId !== awayTeamId;
  const isCurrentUserHomePremium = isCurrentUserHome && isCurrentPremium;
  const isCurrentUserAwayPremium = isCurrentUserAway && isCurrentPremium;
  const isOpponentUserHomePremium = isOpponentUserHome && isOpponentUserPremium;
  const isOpponentUserAwayPremium = isOpponentUserAway && isOpponentUserPremium;

  const isCurrentHomeUserHasKit = isCurrentUserHome && !isEmpty(teamKit);
  const isCurrentAwayUserHasKit = isCurrentUserAway && !isEmpty(teamKit);
  const isOpponentHomeUserHasKit = isOpponentUserHome && !isEmpty(opponentKit);
  const isOpponentAwayUserHasKit = isOpponentUserAway && !isEmpty(opponentKit);
  const isHomeJerseyPremium = isCurrentUserHomePremium || isOpponentUserHomePremium;
  const isAwayJerseyPremium = isCurrentUserAwayPremium || isOpponentUserAwayPremium;
  const matchPayload = data?.[matchData];
  const reportPath = matchOpenPath(matchPayload);
  const openReport = () => {
    if (reportPath) history.push(reportPath);
  };
  const isLive = isCurrentMatchAvailable;
  const crestFromCalendar = (id, name) =>
    (calendarEvents || []).find(
      (event) => event.team?._id === id || event.team?.name === name
    )?.team?.symbol;
  const lastHome = data?.last_match?.results?.home_team;
  const lastAway = data?.last_match?.results?.away_team;
  const crestFromLast = (id, name) => {
    if (lastHome?._id === id || lastHome?.name === name) return lastHome?.symbol;
    if (lastAway?._id === id || lastAway?.name === name) return lastAway?.symbol;
    return null;
  };
  const crestFromFixtures = (id, name) => {
    const days = Array.isArray(fixtures?.matches) ? fixtures.matches : [];
    for (let i = 0; i < days.length; i += 1) {
      const day = days[i];
      const list = Array.isArray(day?.matches) ? day.matches : day?.home_team ? [day] : [];
      for (let j = 0; j < list.length; j += 1) {
        const match = list[j];
        if (match.home_team?._id === id || match.home_team?.name === name) {
          return match.home_team?.symbol;
        }
        if (match.away_team?._id === id || match.away_team?.name === name) {
          return match.away_team?.symbol;
        }
      }
    }
    return null;
  };
  const homeCrest =
    matchPayload?.home_team?.symbol ||
    lookupCrest(data, homeTeam, homeTeamId) ||
    crestFromCalendar(homeTeamId, homeTeam) ||
    crestFromLast(homeTeamId, homeTeam) ||
    crestFromFixtures(homeTeamId, homeTeam);
  const awayCrest =
    matchPayload?.away_team?.symbol ||
    lookupCrest(data, awayTeam, awayTeamId) ||
    crestFromCalendar(awayTeamId, awayTeam) ||
    crestFromLast(awayTeamId, awayTeam) ||
    crestFromFixtures(awayTeamId, awayTeam);

  const { home: homeScore, away: awayScore, hasScore: hasLiveScore } = liveScores(matchPayload);

  const matchTypeLabel = isLive
    ? liveTypeLabel(matchPayload)
    : noNextMatch
      ? 'Off season'
      : isDomesticCup
        ? 'Cup'
        : 'League';

  return (
    <StyledContainer>
      <SectionHeading
        title={isLive ? 'Live match' : 'Next match'}
        action={<StyledTypePill>{matchTypeLabel}</StyledTypePill>}
      />
      <FallbackContentAnimation
        loading={isLoading}
        fallback={
          <StyledFallbackContainer>
            {isMobile ? <MobileNextMatchPreloader /> : <NextMatchPreloader />}
          </StyledFallbackContainer>
        }
      >
        <StyledNextMatch className="next-match-section">
          {isLive ? (
            <>
              <StyledDatePill $live>{liveMinuteLabel(matchPayload, { withWord: true })}</StyledDatePill>
              <StyledNextMatchInner $live>
                <StyledKitsRow>
                  <StyledKitSide $side="home">
                    <StyledKitStage>
                      {homeCrest ? <StyledCrestFade $side="home" src={homeCrest} alt="" /> : null}
                      <TeamShirt
                        isPremium={isHomeJerseyPremium}
                        style={kitStyle}
                        team={homeTeam}
                        teamKit={isCurrentHomeUserHasKit ? teamKit?.pic : isOpponentHomeUserHasKit ? opponentKit?.pic : null}
                      />
                    </StyledKitStage>
                    <StyledKitName $side="home">{homeTeam}</StyledKitName>
                  </StyledKitSide>
                  {hasLiveScore ? (
                    <StyledLiveScore
                      as={reportPath ? 'button' : 'div'}
                      type={reportPath ? 'button' : undefined}
                      $clickable={!!reportPath}
                      onClick={reportPath ? openReport : undefined}
                      title={reportPath ? 'Open match' : undefined}
                    >
                      <StyledLiveGoal>{homeScore}</StyledLiveGoal>
                      <StyledLiveX>×</StyledLiveX>
                      <StyledLiveGoal>{awayScore}</StyledLiveGoal>
                    </StyledLiveScore>
                  ) : (
                    <StyledVs $live>×</StyledVs>
                  )}
                  <StyledKitSide $side="away">
                    <StyledKitStage>
                      {awayCrest ? <StyledCrestFade $side="away" src={awayCrest} alt="" /> : null}
                      <TeamShirt
                        isPremium={isAwayJerseyPremium}
                        style={kitStyle}
                        team={awayTeam}
                        teamKit={isCurrentAwayUserHasKit ? teamKit?.pic : isOpponentAwayUserHasKit ? opponentKit?.pic : null}
                      />
                    </StyledKitStage>
                    <StyledKitName $side="away">{awayTeam}</StyledKitName>
                  </StyledKitSide>
                </StyledKitsRow>
                <StyledButtonsContainer $live>
                  <StyledInGame $live to="/team/strategy">
                    In-game strategy
                  </StyledInGame>
                  <LiveMatch
                    variant="watch"
                    discordChannelId={data.discordChannel}
                    isAuthenticated={isAuthenticated}
                  />
                </StyledButtonsContainer>
              </StyledNextMatchInner>
            </>
          ) : noNextMatch ? (
            <StyledNextMatchInner>
              <StyledEmpty>Next match starts next Monday</StyledEmpty>
            </StyledNextMatchInner>
          ) : (
            <>
              <StyledDatePill>
                {timezoneDateRelatedToUTC}
              </StyledDatePill>
              <StyledNextMatchInner>
                <StyledKitsRow>
                <StyledKitSide $side="home">
                  <StyledKitStage>
                    {homeCrest ? <StyledCrestFade $side="home" src={homeCrest} alt="" /> : null}
                    <TeamShirt
                      isPremium={isHomeJerseyPremium}
                      style={kitStyle}
                      team={homeTeam}
                      teamKit={isCurrentHomeUserHasKit ? teamKit?.pic : isOpponentHomeUserHasKit ? opponentKit?.pic : null}
                    />
                  </StyledKitStage>
                  <StyledKitName $side="home">{homeTeam}</StyledKitName>
                </StyledKitSide>
                <StyledVs>×</StyledVs>
                <StyledKitSide $side="away">
                  <StyledKitStage>
                    {awayCrest ? <StyledCrestFade $side="away" src={awayCrest} alt="" /> : null}
                    <TeamShirt
                      isPremium={isAwayJerseyPremium}
                      style={kitStyle}
                      team={awayTeam}
                      teamKit={isCurrentAwayUserHasKit ? teamKit?.pic : isOpponentAwayUserHasKit ? opponentKit?.pic : null}
                    />
                  </StyledKitStage>
                  <StyledKitName $side="away">{awayTeam}</StyledKitName>
                </StyledKitSide>
              </StyledKitsRow>
              <StyledButtonsContainer>
                <StyledLink to="/team/strategy">Set strategy</StyledLink>
              </StyledButtonsContainer>
              </StyledNextMatchInner>
            </>
          )}
        </StyledNextMatch>
      </FallbackContentAnimation>
    </StyledContainer>
  );
};

NextMatch.propTypes = {
  data: PropTypes.object,
};

NextMatch.defaultProps = {
  data: null,
};

export default React.memo(NextMatch);
